import {useState, useEffect, useMemo} from 'react'
import {Plus, ChevronsDown, ChevronsUp, Search, ShieldCheck, Bot, KeyRound} from 'lucide-react'
import * as API from '../../api'
import {PageTitle} from '../../../_metronic/layout/core'
import ConfirmModal from '../modals/ConfirmModal'
import PermissionsModal from './PermissionModal'
import {EmptyState} from './EmptyState'
import {LoadingState} from './LoadingState'
import {PermissionCard} from './PermissionCard'
import {isAgentStudioPermission, type Permission} from './permissionMetadata'

type Props = {
  className?: string
}

const PermissionsScreen: React.FC<Props> = ({className}) => {
  const [message, setMessage] = useState<string>('')
  const [permissionModal, showPermissionModal] = useState(false)
  const [confirmModal, showConfirmModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [groupPermissions, setGroupPermissions] = useState<any>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({})
  const [groupSearch, setGroupSearch] = useState('')
  const [groupFilter, setGroupFilter] = useState<'all' | 'agent-studio'>('all')
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    showConfirmModal(message !== '')
  }, [message])

  const permissionMap = useMemo(
    () => new Map(permissions.map((permission) => [permission.id_feature, permission])),
    [permissions]
  )
  const filteredGroups = useMemo(() => {
    const normalized = groupSearch.trim().toLocaleLowerCase('pt-BR')
    return groupPermissions.filter((group: any) => {
      const groupPermissionsData = group.data
        .map((permissionId: string | number) => permissionMap.get(permissionId))
        .filter((permission: Permission | undefined): permission is Permission => !!permission)
      const matchesSearch =
        !normalized ||
        group.name.toLocaleLowerCase('pt-BR').includes(normalized) ||
        groupPermissionsData.some(
          (permission: Permission) =>
            permission.title.toLocaleLowerCase('pt-BR').includes(normalized) ||
            permission.name.toLowerCase().includes(normalized)
        )
      const matchesFilter =
        groupFilter === 'all' ||
        groupPermissionsData.some((permission: Permission) => isAgentStudioPermission(permission))
      return matchesSearch && matchesFilter
    })
  }, [groupPermissions, groupFilter, groupSearch, permissionMap])

  const agentStudioGroupCount = groupPermissions.filter((group: any) =>
    group.data.some((permissionId: string | number) => {
      const permission = permissionMap.get(permissionId)
      return permission ? isAgentStudioPermission(permission) : false
    })
  ).length
  const totalAssignments = groupPermissions.reduce(
    (total: number, group: any) => total + group.data.length,
    0
  )
  const loadData = async () => {
    setIsLoading(true)
    setLoadError('')
    try {
      const [permissionsRes, groupPermissionsRes] = await Promise.all([
        API.getPermissions(),
        API.getGroupPermissions(),
      ])

      setPermissions(permissionsRes.data)
      const groupsData = groupPermissionsRes.data.map((item: any) => ({
        ...item,
        data: Array.isArray(item.data) ? item.data : JSON.parse(item.data || '[]'),
      }))
      setGroupPermissions(groupsData)

      const initialExpandedState = groupsData.reduce((acc: Record<string, boolean>, group: any) => {
        acc[group.id_feature_group] = false
        return acc
      }, {})
      setExpandedCards(initialExpandedState)
    } catch (error) {
      console.error('Error loading data:', error)
      setLoadError('Não foi possível carregar os grupos de permissões.')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleCard = (id: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const expandAll = () => {
    const allExpanded = filteredGroups.reduce((acc: Record<string, boolean>, group: any) => {
      acc[group.id_feature_group] = true
      return acc
    }, {})
    setExpandedCards(allExpanded)
  }

  const collapseAll = () => {
    const allCollapsed = filteredGroups.reduce((acc: Record<string, boolean>, group: any) => {
      acc[group.id_feature_group] = false
      return acc
    }, {})
    setExpandedCards(allCollapsed)
  }

  const allExpanded =
    filteredGroups.length > 0 &&
    filteredGroups.every((group: any) => expandedCards[group.id_feature_group])
  const allCollapsed =
    filteredGroups.length > 0 &&
    filteredGroups.every((group: any) => !expandedCards[group.id_feature_group])

  const handleNewGroup = () => {
    setSelectedItem(null)
    showPermissionModal(true)
  }

  const handleEditGroup = (group: any) => {
    setSelectedItem(group)
    showPermissionModal(true)
  }

  const handleDeleteGroup = (group: any) => {
    setSelectedItem(group)
    setMessage(
      'Deseja remover esse grupo de permissões? Caso exista usuários vinculados a ele, eles serão afetados por essa ação.'
    )
  }

  const removeItem = async (item: any) => {
    setIsLoading(true)
    try {
      await API.removeGroupPermissions(item)
      await loadData()
    } catch (error) {
      console.error('Error removing group:', error)
    }
  }

  return (
    <>
      <div className={'container mx-auto px-4 py-8 ' + (className || '')}>
        <div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <PageTitle>Grupos de Permissões</PageTitle>
            <p className='mt-1 text-sm text-gray-500'>
              Monte perfis de acesso por sistema, tela e ação sensível.
            </p>
          </div>
          <button
            onClick={handleNewGroup}
            className='inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md'
          >
            <Plus className='w-5 h-5 mr-2' />
            Criar grupo de permissões
          </button>
        </div>

        {!isLoading && !loadError && groupPermissions.length > 0 && (
          <div className='mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3'>
            <div className='flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm'>
              <span className='rounded-lg bg-blue-50 p-2.5 text-blue-600'>
                <ShieldCheck className='h-5 w-5' />
              </span>
              <div>
                <p className='text-xl font-bold text-gray-900'>{groupPermissions.length}</p>
                <p className='text-xs text-gray-500'>grupos cadastrados</p>
              </div>
            </div>
            <div className='flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 shadow-sm'>
              <span className='rounded-lg bg-blue-100 p-2.5 text-blue-700'>
                <Bot className='h-5 w-5' />
              </span>
              <div>
                <p className='text-xl font-bold text-gray-900'>{agentStudioGroupCount}</p>
                <p className='text-xs text-gray-500'>grupos com Agent Studio</p>
              </div>
            </div>
            <div className='flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm'>
              <span className='rounded-lg bg-purple-50 p-2.5 text-purple-600'>
                <KeyRound className='h-5 w-5' />
              </span>
              <div>
                <p className='text-xl font-bold text-gray-900'>{totalAssignments}</p>
                <p className='text-xs text-gray-500'>permissões atribuídas</p>
              </div>
            </div>
          </div>
        )}
        {isLoading ? (
          <LoadingState />
        ) : loadError ? (
          <div className='rounded-xl border border-red-200 bg-red-50 p-6 text-center'>
            <p className='text-sm font-semibold text-red-700'>{loadError}</p>
            <button
              type='button'
              onClick={loadData}
              className='mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700'
            >
              Tentar novamente
            </button>
          </div>
        ) : groupPermissions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className='mb-4'>
              <div className='flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between'>
                <div className='relative w-full lg:max-w-xl'>
                  <Search className='absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                  <input
                    type='search'
                    placeholder='Buscar grupo, permissão ou identificador...'
                    value={groupSearch}
                    onChange={(event) => setGroupSearch(event.target.value)}
                    className='w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100'
                  />
                </div>
                <div className='flex flex-wrap gap-2'>
                  <button
                    type='button'
                    onClick={() => setGroupFilter('all')}
                    className={
                      'rounded-lg px-3 py-2 text-xs font-semibold transition ' +
                      (groupFilter === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                    }
                  >
                    Todos os grupos
                  </button>
                  <button
                    type='button'
                    onClick={() => setGroupFilter('agent-studio')}
                    className={
                      'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ' +
                      (groupFilter === 'agent-studio'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100')
                    }
                  >
                    <Bot className='h-4 w-4' /> Com Agent Studio
                  </button>
                </div>
              </div>
            </div>
            <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <div className='text-sm text-gray-600'>
                <span className='font-semibold text-gray-900'>{filteredGroups.length}</span> de{' '}
                {groupPermissions.length} grupos exibidos
              </div>
              <div className='flex gap-2'>
                <button
                  onClick={expandAll}
                  disabled={allExpanded}
                  className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm'
                >
                  <ChevronsDown className='w-4 h-4' />
                  Expandir Todos
                </button>
                <button
                  onClick={collapseAll}
                  disabled={allCollapsed}
                  className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm'
                >
                  <ChevronsUp className='w-4 h-4' />
                  Colapsar Todos
                </button>
              </div>
            </div>

            <div className='grid grid-cols-1 gap-6'>
              {filteredGroups.length === 0 && (
                <div className='rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-sm text-gray-500'>
                  Nenhum grupo corresponde à busca ou ao filtro selecionado.
                </div>
              )}
              {filteredGroups.map((group: any) => (
                <PermissionCard
                  key={group.id_feature_group}
                  group={group}
                  permissions={permissions}
                  onEdit={handleEditGroup}
                  onDelete={handleDeleteGroup}
                  isExpanded={expandedCards[group.id_feature_group]}
                  onToggleExpand={() => toggleCard(group.id_feature_group)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {permissionModal && (
        <PermissionsModal
          data={permissions}
          group={selectedItem}
          onClose={() => {
            showPermissionModal(false)
            setSelectedItem(null)
          }}
          onChange={() => {
            loadData()
            showPermissionModal(false)
            setSelectedItem(null)
          }}
        />
      )}

      {confirmModal && (
        <div className='fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50'>
          <ConfirmModal
            title={`Grupo: ${selectedItem?.name}`}
            message={message}
            onClose={(value: boolean) => {
              if (value) removeItem(selectedItem)
              setSelectedItem(null)
              setMessage('')
            }}
          />
        </div>
      )}
    </>
  )
}

export default PermissionsScreen
